import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Briefcase, Warning, CheckCircle, Clock, Certificate, HardHat, IdentificationCard, Money, Receipt, Signature as SignatureIcon, ClockCounterClockwise, X, Plus } from "@phosphor-icons/react";
import { api, apiError } from "@/lib/api";
import ProjectBadge from "@/components/ProjectBadge";
import { useLang } from "@/i18n";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import SignaturePad from "@/components/SignaturePad";
import MyShiftDialog from "@/components/MyShiftDialog";
import { findEquipment, SHIFT_LABEL } from "@/lib/operationsCatalog";
import { MyEppSection } from "@/pages/MyHr";

import MyWhatsAppPanel from "@/components/MyWhatsAppPanel";

const STATUS_STYLES = {
  delayed: { cls: "bg-red-50 text-red-700 border-red-200", dot: "bg-red-500" },
  at_risk: { cls: "bg-amber-50 text-amber-800 border-amber-200", dot: "bg-amber-500" },
  in_progress: { cls: "bg-blue-50 text-blue-900 border-blue-200", dot: "bg-blue-800" },
  upcoming: { cls: "bg-slate-50 text-slate-600 border-slate-200", dot: "bg-slate-400" },
  early: { cls: "bg-emerald-50 text-emerald-700 border-emerald-200", dot: "bg-emerald-500" },
  completed: { cls: "bg-slate-100 text-slate-500 border-slate-200", dot: "bg-slate-400" },
};

const PRIORITY_STYLES = {
  high: "bg-red-100 text-red-800 border-red-200",
  medium: "bg-amber-100 text-amber-800 border-amber-200",
  low: "bg-slate-100 text-slate-600 border-slate-200",
};

export default function MyWork() {
  const { t } = useLang();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [receipts, setReceipts] = useState([]);
  const [myShifts, setMyShifts] = useState([]);
  const [weekDestajo, setWeekDestajo] = useState(null);   // Iter120 · preview semanal
  const [shiftDlgOpen, setShiftDlgOpen] = useState(false);
  const [signing, setSigning] = useState(null);        // receipt currently being signed
  const [signMode, setSignMode] = useState("admin");   // "admin" | "worker"
  const [sigData, setSigData] = useState("");
  const [sigNote, setSigNote] = useState("");
  const [sigBusy, setSigBusy] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    api.get("/me/workspace")
      .then((r) => setData(r.data))
      .finally(() => setLoading(false));
    api.get("/me/payroll-receipts?weeks_back=8")
      .then((r) => setReceipts(r.data.receipts || []))
      .catch(() => setReceipts([]));
    api.get("/me/operations?days=14")
      .then((r) => setMyShifts(r.data.entries || []))
      .catch(() => setMyShifts([]));
    api.get("/me/destajo-week")
      .then((r) => setWeekDestajo(r.data))
      .catch(() => setWeekDestajo(null));
  }, []);
  useEffect(() => { load(); }, [load]);

  const isAdmin = ["admin", "manager"].includes(user?.role);
  const removeShift = async (id) => {
    if (!window.confirm("¿Eliminar esta captura de turno? La acción quedará en el historial de auditoría.")) return;
    try {
      await api.delete(`/operations/entries/${id}`, {
        headers: { "X-Delete-Reason": `Eliminado desde Mi Trabajo por administrador ${user?.name || ""}` },
      });
      toast.success("Turno eliminado");
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const openSign = (r, mode = "admin") => { setSigning(r); setSignMode(mode); setSigData(""); setSigNote(""); };
  const submitSign = async () => {
    if (!sigData) { toast.warning("Firma antes de aceptar"); return; }
    setSigBusy(true);
    try {
      const endpoint = signMode === "worker"
        ? `/me/payroll-receipts/${signing.folio}/worker-sign`
        : `/me/payroll-receipts/${signing.folio}/sign`;
      await api.post(endpoint, {
        signature_data_url: sigData, note: sigNote,
      });
      toast.success(signMode === "worker"
        ? "Recibo firmado — la firma queda registrada en tu PDF"
        : "Recibo firmado — gracias");
      setSigning(null);
      load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setSigBusy(false); }
  };

  const receiptStats = useMemo(() => {
    const unsigned = receipts.filter((r) => !r.signed_at).length;
    const netTotal = receipts.reduce((a, r) => a + (r.net || 0), 0);
    return { unsigned, netTotal, count: receipts.length };
  }, [receipts]);

  if (loading || !data) return <p className="text-slate-500">{t("loading")}</p>;
  const c = data.counts;
  const emp = data.employee;

  return (
    <div className="space-y-6" data-testid="my-work-page">
      {/* Header */}
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="overline-label">{t("myWork")}</p>
          <h1 className="font-heading font-black text-4xl tracking-tight text-slate-900 mt-1" data-testid="my-work-title">
            {t("welcomeBack")}, {user?.name}
          </h1>
          <p className="text-sm text-slate-500 mt-2 max-w-2xl">{t("myWorkHint")}</p>
        </div>
        {emp && (
          <div data-testid="my-hr-badge" className="bg-white border border-slate-200 rounded-lg px-4 py-3">
            <div className="flex items-center gap-2 mb-1">
              <IdentificationCard size={14} weight="bold" className="text-blue-900" />
              <p className="overline-label">{t("hrProfile")}</p>
            </div>
            <p className="font-semibold text-sm text-slate-900">{emp.position || emp.role}</p>
            <p className="text-xs text-slate-500">
              {emp.area && <span>{emp.area}</span>}
              {emp.crew && emp.crew !== "N/A" && <span> · Cuadrilla {emp.crew}</span>}
              {emp.regime && <span> · {emp.regime}</span>}
            </p>
          </div>
        )}
      </div>

      {/* KPI ribbon */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Kpi testid="kpi-my-tasks" icon={<Briefcase size={16} weight="fill" className="text-blue-900" />}
          label={t("myTasks")} value={c.tasks_total} sub={c.tasks_delayed > 0 ? `${c.tasks_delayed} ${t("delayed").toLowerCase()}` : t("taskStatusOnTrack")}
          subCls={c.tasks_delayed > 0 ? "text-red-600" : "text-emerald-600"} />
        <Kpi testid="kpi-my-actions" icon={<HardHat size={16} weight="fill" className="text-amber-700" />}
          label={t("myActions")} value={c.actions_total} sub={c.actions_overdue > 0 ? `${c.actions_overdue} ${t("overdue").toLowerCase()}` : "—"}
          subCls={c.actions_overdue > 0 ? "text-red-600" : "text-slate-500"} />
        <Kpi testid="kpi-my-certs-expired" icon={<Warning size={16} weight="fill" className="text-red-600" />}
          label={`${t("myCerts")} · ${t("expired")}`} value={c.certs_expired} sub={c.certs_expired === 0 ? t("noMyCerts").split(".")[0] : "renovar"}
          subCls={c.certs_expired > 0 ? "text-red-600" : "text-emerald-600"} />
        <Kpi testid="kpi-my-certs-expiring" icon={<Certificate size={16} weight="fill" className="text-amber-600" />}
          label={`${t("myCerts")} · próx.`} value={c.certs_expiring} sub="90 días" subCls="text-slate-500" />
      </div>

      <MyWhatsAppPanel />

      {/* Tasks */}
      <Section title={t("myTasks")} testid="my-tasks-section" icon={<Briefcase size={16} weight="duotone" className="text-blue-900" />} count={data.tasks_assigned.length}>
        {data.tasks_assigned.length === 0 ? (
          <EmptyState testid="my-tasks-empty" message={t("noMyTasks")} />
        ) : (
          <ul className="divide-y divide-slate-100">
            {data.tasks_assigned.map((tk) => {
              const style = STATUS_STYLES[tk.status_kind] || STATUS_STYLES.upcoming;
              return (
                <li key={tk.id} data-testid={`my-task-${tk.id}`} className="py-3 flex items-center gap-4">
                  <span className={`h-2 w-2 rounded-full flex-shrink-0 ${style.dot}`} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <Link to={`/projects/${tk.project_id}`} className="font-semibold text-slate-900 hover:text-blue-900 hover:underline truncate">{tk.name}</Link>
                      <span className={`text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 rounded-full border ${style.cls}`}>
                        {t(`taskStatus${tk.status_kind[0].toUpperCase() + tk.status_kind.slice(1).replace(/_(\w)/g, (_, x) => x.toUpperCase())}`) || tk.status_kind}
                        {tk.status_days > 0 && ` · ${tk.status_days}d`}
                      </span>
                    </div>
                    <p className="text-xs text-slate-500 mt-0.5">
                      <Link to={`/projects/${tk.project_id}`} className="hover:text-slate-800">{tk.project_code}</Link>
                      {" · "}{tk.phase_label} · {tk.start_date} → {tk.end_date}
                    </p>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <div className="h-1.5 w-24 bg-slate-100 rounded-full overflow-hidden">
                      <div className="h-full bg-blue-900" style={{ width: `${tk.progress}%` }} />
                    </div>
                    <span className="text-xs font-mono text-slate-500 w-9 text-right">{tk.progress}%</span>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </Section>

      {/* HSE Actions */}
      <Section title={t("myActions")} testid="my-actions-section" icon={<HardHat size={16} weight="duotone" className="text-amber-700" />} count={data.hse_actions.length}>
        {data.hse_actions.length === 0 ? (
          <EmptyState testid="my-actions-empty" message={t("noMyActions")} />
        ) : (
          <ul className="divide-y divide-slate-100">
            {data.hse_actions.map((a) => {
              const today = new Date().toISOString().slice(0, 10);
              const isOverdue = a.due_date && a.due_date < today;
              return (
                <li key={a.id} data-testid={`my-action-${a.id}`} className="py-3 flex items-start gap-4">
                  <Clock size={18} weight="duotone" className={`flex-shrink-0 mt-0.5 ${isOverdue ? "text-red-500" : "text-slate-400"}`} />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-slate-900 leading-snug">{a.description}</p>
                    <div className="flex items-center gap-2 mt-1 flex-wrap">
                      <span className={`text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 rounded-full border ${PRIORITY_STYLES[a.priority] || PRIORITY_STYLES.medium}`}>
                        {t(`priority${a.priority[0].toUpperCase() + a.priority.slice(1)}`) || a.priority}
                      </span>
                      <span className={`text-xs font-mono ${isOverdue ? "text-red-600 font-bold" : "text-slate-500"}`}>
                        {isOverdue ? t("overdue") : t("dueIn")} · {a.due_date || "—"}
                      </span>
                      {a.source_type && (
                        <Link to="/hse" className="text-[10px] text-slate-400 uppercase tracking-wider hover:text-blue-900">
                          {a.source_type}
                        </Link>
                      )}
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </Section>

      {/* Certifications */}
      <Section title={t("myCerts")} testid="my-certs-section" icon={<Certificate size={16} weight="duotone" className="text-emerald-700" />} count={data.certs_expiring.length}>
        {!emp ? (
          <EmptyState testid="my-certs-empty" message={t("notInHR")} />
        ) : data.certs_expiring.length === 0 ? (
          <div className="py-6 flex items-center justify-center gap-3 text-emerald-700" data-testid="my-certs-ok">
            <CheckCircle size={20} weight="fill" />
            <p className="text-sm font-semibold">{t("noMyCerts")}</p>
          </div>
        ) : (
          <ul className="divide-y divide-slate-100">
            {data.certs_expiring.map((c, i) => (
              <li key={i} data-testid={`my-cert-${i}`} className="py-3 flex items-center gap-4">
                <div className={`h-8 w-8 rounded-md flex items-center justify-center ${c.expired ? "bg-red-100 text-red-700" : c.days_left <= 30 ? "bg-amber-100 text-amber-800" : "bg-slate-100 text-slate-600"}`}>
                  <Certificate size={16} weight="bold" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-slate-900 text-sm">{c.name}</p>
                  <p className="text-xs text-slate-500">{c.issuer || "—"} · Vence {c.expires_date}</p>
                </div>
                <span className={`text-xs font-bold ${c.expired ? "text-red-700" : c.days_left <= 30 ? "text-amber-700" : "text-slate-500"}`}>
                  {c.expired ? `${Math.abs(c.days_left)}d ${t("overdue").toLowerCase()}` : `${c.days_left} ${t("daysLeft")}`}
                </span>
              </li>
            ))}
          </ul>
        )}
      </Section>

      {/* Iter120 · Preview Semanal de Destajo del operador */}
      {weekDestajo && weekDestajo.employee && (
        <div className="rounded-lg border border-emerald-200 bg-gradient-to-br from-emerald-50 to-emerald-100/40 p-4"
             data-testid="my-destajo-week">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <Money size={18} weight="duotone" className="text-emerald-700" />
              <p className="font-heading font-bold text-emerald-900 uppercase text-xs tracking-widest">
                Mi destajo · esta semana
              </p>
              <span className="text-[10px] text-emerald-700 hidden sm:inline">
                {weekDestajo.week_start} → {weekDestajo.week_end}
              </span>
            </div>
            <p className="font-heading font-black text-2xl text-emerald-800 tabular-nums"
               data-testid="my-destajo-week-total">
              ${(weekDestajo.total_gross || 0).toLocaleString("es-MX", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </p>
          </div>
          {weekDestajo.shifts.length === 0 ? (
            <p className="text-[11px] text-emerald-800/80 italic">
              Aún no tienes destajo esta semana. Cada turno que captures aquí se calcula con las tarifas vigentes.
            </p>
          ) : (
            <ul className="space-y-1 text-[11px] text-emerald-900">
              {weekDestajo.shifts.map((s) => (
                <li key={s.id} className="flex justify-between gap-2 py-0.5 border-b border-emerald-200/60 last:border-0"
                    data-testid={`my-destajo-shift-${s.id}`}>
                  <span className="truncate">
                    <b>{s.date}</b> · {SHIFT_LABEL[s.shift] || s.shift} · {s.equipment_type} · <i>{s.role}</i>
                  </span>
                  <span className="font-mono font-bold shrink-0">${s.gross.toLocaleString("es-MX", { minimumFractionDigits: 2 })}</span>
                </li>
              ))}
            </ul>
          )}
          <p className="text-[10px] italic text-emerald-700 mt-2">
            Estimado con tarifas vigentes por fecha del turno. El monto final se cierra con las cláusulas del auditor y descuentos del kiosko.
          </p>
        </div>
      )}

      {/* Mis Turnos capturados en Operaciones */}
      <Section title="Mis Turnos" testid="my-shifts-section"
        icon={<HardHat size={16} weight="duotone" className="text-amber-600" />}
        count={myShifts.length}
        action={
          <Button size="sm" className="bg-blue-900 hover:bg-blue-800 text-white gap-1.5 h-7"
            onClick={() => setShiftDlgOpen(true)} data-testid="my-shift-new">
            <Plus size={12} weight="bold" /> Capturar mi turno
          </Button>
        }>
        {myShifts.length === 0 ? (
          <EmptyState testid="my-shifts-empty"
            message="Aún no has capturado turnos en los últimos 14 días. Usa «Capturar mi turno» para registrar tu producción diaria." />
        ) : (
          <ul className="divide-y divide-slate-100">
            {myShifts.slice(0, 10).map((s) => {
              const eqc = findEquipment(s.equipment_type);
              const Icon = eqc?.icon || HardHat;
              const metricsSummary = Object.entries(s.metrics || {})
                .filter(([k, v]) => v && !k.startsWith("horometro_") && !["demora_inicio", "demora_fin", "demora_motivo", "origen", "destino"].includes(k))
                .slice(0, 4)
                .map(([k, v]) => `${k}: ${v}`)
                .join(" · ");
              return (
                <li key={s.id} data-testid={`my-shift-row-${s.id}`} className="py-3">
                  <div className="flex items-center gap-3">
                    <div className={`h-9 w-9 rounded-md ${eqc?.color || "bg-slate-100"} flex items-center justify-center flex-shrink-0`}>
                      <Icon size={16} weight="bold" className="text-slate-700" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="font-semibold text-slate-900 text-sm">{eqc?.label || s.equipment_type}</p>
                        <span className="font-mono text-[10px] text-slate-500">{s.equipment_code}</span>
                        <ProjectBadge id={s.project_id} />
                        <span className="text-[10px] uppercase tracking-widest font-bold px-1.5 py-0.5 rounded bg-slate-100 text-slate-600">
                          {s.date} · {SHIFT_LABEL[s.shift] || s.shift}
                        </span>
                        {s.area && <span className="text-[10px] text-slate-500">{s.area}</span>}
                        {/* Chip de fin de turno */}
                        {(s.metrics?.end_status === "no_operativo") ? (
                          <span data-testid={`my-shift-end-status-${s.id}`}
                                className="text-[10px] uppercase tracking-widest font-black px-2 py-0.5 rounded-full bg-red-100 text-red-700 border border-red-200">
                            ✗ No operativo
                          </span>
                        ) : s.metrics?.end_status && (
                          <span data-testid={`my-shift-end-status-${s.id}`}
                                className="text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 border border-emerald-200">
                            ✓ Operativo
                          </span>
                        )}
                      </div>
                      {metricsSummary && <p className="text-[11px] text-slate-600 mt-0.5 truncate">{metricsSummary}</p>}
                      {(s.metrics?.end_location || s.metrics?.end_reason) && (
                        <p className="text-[11px] mt-0.5">
                          {s.metrics?.end_location && (
                            <span className="text-slate-500"><b className="text-slate-700">Ubicación al cierre:</b> {s.metrics.end_location}</span>
                          )}
                          {s.metrics?.end_reason && (
                            <span className="text-red-600 ml-2"><b>Motivo:</b> {s.metrics.end_reason}</span>
                          )}
                        </p>
                      )}
                    </div>
                    <div className="text-right flex items-center gap-2">
                      <div>
                        <p className="font-heading font-black text-lg text-blue-900">{s.hours || 0}<span className="text-xs text-slate-500 font-normal ml-1">h</span></p>
                      </div>
                      {isAdmin && (
                        <button
                          onClick={() => removeShift(s.id)}
                          data-testid={`my-shift-delete-${s.id}`}
                          className="text-slate-400 hover:text-red-600 p-1"
                          title="Eliminar captura (solo administradores)"
                        >
                          <X size={16} weight="bold" />
                        </button>
                      )}
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </Section>

      {/* Mi EPP · Portal del colaborador */}
      <MyEppSection />

      {/* Mis Recibos Semanales — destajo pagado con firma digital */}
      <Section title="Mis Recibos Semanales" testid="my-receipts-section"
        icon={<Receipt size={16} weight="duotone" className="text-emerald-700" />} count={receipts.length}>
        {receipts.length === 0 ? (
          <EmptyState testid="my-receipts-empty"
            message={emp ? "Aún no tienes producción registrada en Operaciones para las últimas semanas" : "Vincula tu usuario a Nómina para ver tus recibos"} />
        ) : (
          <>
            <div className="grid grid-cols-3 gap-3 py-3">
              <MiniKpi label="Recibos" value={receiptStats.count} testid="my-receipts-count" />
              <MiniKpi label="Sin firmar" value={receiptStats.unsigned}
                tone={receiptStats.unsigned > 0 ? "amber" : "slate"} testid="my-receipts-unsigned" />
              <MiniKpi label="NETO acumulado" tone="emerald"
                value={`$${receiptStats.netTotal.toLocaleString("es-MX", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
                testid="my-receipts-net" />
            </div>
            <ul className="divide-y divide-slate-100">
              {receipts.map((r) => {
                const hasClauses = (r.deductions_total || 0) > 0;
                const blocked = hasClauses && (r.net || 0) < 1;   // pago anulado
                return (
                <li key={r.folio} data-testid={`my-receipt-${r.folio}`} className="py-3 space-y-2">
                  <div className="flex items-center gap-4">
                    <div className={`h-10 w-10 rounded-md flex items-center justify-center flex-shrink-0
                      ${blocked ? "bg-red-100 text-red-700"
                        : r.signed_at ? "bg-emerald-100 text-emerald-700"
                        : hasClauses ? "bg-amber-100 text-amber-700"
                        : "bg-amber-100 text-amber-700"}`}>
                      {blocked ? <Warning size={20} weight="fill" />
                        : r.signed_at ? <CheckCircle size={20} weight="fill" />
                        : <ClockCounterClockwise size={18} weight="bold" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="font-semibold text-slate-900 font-mono text-sm">{r.folio}</p>
                        <span className="text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 rounded-full border bg-white border-slate-200 text-slate-600">
                          {r.date_from} → {r.date_to}
                        </span>
                        {blocked && (
                          <span data-testid={`my-receipt-blocked-${r.folio}`}
                                className="text-[10px] uppercase tracking-widest font-black px-2 py-0.5 rounded-full bg-red-600 text-white animate-pulse">
                            ⚠ Pago no procede
                          </span>
                        )}
                        {!blocked && r.signed_at && (
                          <span className="text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800">
                            Firmado {r.signed_at.slice(0, 10)} · {r.signed_by || "admin"}
                          </span>
                        )}
                        {!blocked && !r.signed_at && (
                          <span className="text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 rounded-full bg-amber-100 text-amber-800">
                            En revisión
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-slate-500 mt-0.5">
                        {r.days_worked} día(s) · {r.concepts.length} concepto(s) ·
                        Bruto ${r.gross.toLocaleString("es-MX", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        {r.deductions_total > 0 && (
                          <> · <span className="text-red-600 font-bold">
                            Desc. cláusulas auditor: -${r.deductions_total.toLocaleString("es-MX", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </span></>
                        )}
                      </p>

                      {/* Barra apilada: composición del pago (destajo neto vs descuentos) */}
                      {r.gross > 0 && (() => {
                        const netPct = Math.max(0, (r.net / r.gross) * 100);
                        const dedPct = Math.max(0, 100 - netPct);
                        return (
                          <div className="mt-2" data-testid={`my-receipt-breakdown-${r.folio}`}>
                            <div className="flex h-2.5 rounded-full overflow-hidden bg-slate-100 border border-slate-200">
                              <div className="bg-emerald-500" style={{ width: `${netPct}%` }}
                                   title={`Destajo neto (te pagan): $${r.net.toLocaleString("es-MX", { minimumFractionDigits: 2 })}`} />
                              {dedPct > 0 && (
                                <div className="bg-rose-500" style={{ width: `${dedPct}%` }}
                                     title={`Descuento por cláusulas: $${r.deductions_total.toLocaleString("es-MX", { minimumFractionDigits: 2 })}`} />
                              )}
                            </div>
                            <div className="flex justify-between text-[9px] mt-0.5 font-mono">
                              <span className="text-emerald-700 font-bold">
                                ● {netPct.toFixed(0)}% te pagan
                              </span>
                              {dedPct > 0 && (
                                <span className="text-rose-600 font-bold">
                                  ● {dedPct.toFixed(0)}% descontado
                                </span>
                              )}
                            </div>
                          </div>
                        );
                      })()}
                    </div>
                    <div className="text-right">
                      <p className={`font-mono font-black text-lg ${blocked ? "text-red-600 line-through" : "text-emerald-700"}`}>
                        ${r.net.toLocaleString("es-MX", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </p>
                      <div className="flex justify-end items-center gap-1.5 mt-1">
                        {r.worker_id && (
                          <a href={`${process.env.REACT_APP_BACKEND_URL}/api/finance/contractor-payroll/worker-pdf?date_from=${r.date_from}&date_to=${r.date_to}&worker_id=${encodeURIComponent(r.worker_id)}`}
                             onClick={async (e) => {
                               e.preventDefault();
                               try {
                                 const res = await api.get(
                                   `/finance/contractor-payroll/worker-pdf?date_from=${r.date_from}&date_to=${r.date_to}&worker_id=${encodeURIComponent(r.worker_id)}`,
                                   { responseType: "blob" });
                                 const url = URL.createObjectURL(res.data);
                                 const a = document.createElement("a");
                                 a.href = url; a.download = `${r.folio}.pdf`;
                                 a.click(); URL.revokeObjectURL(url);
                               } catch (err) { toast.error(apiError(err)); }
                             }}
                             className="text-[10px] font-bold uppercase tracking-widest px-2 py-1 rounded bg-slate-900 hover:bg-slate-800 text-white gap-1 inline-flex items-center"
                             data-testid={`my-receipt-pdf-${r.folio}`}>
                            <Receipt size={11} weight="bold" /> PDF
                          </a>
                        )}
                        {/* Firma del trabajador — solo si el recibo fue publicado
                            por el admin y el trabajador aún no firmó */}
                        {r.published_at && !r.worker_signed_at && !blocked && (
                          <Button size="sm" className="h-7 bg-emerald-600 hover:bg-emerald-500 text-white text-xs gap-1"
                            onClick={() => openSign(r, "worker")}
                            data-testid={`my-receipt-worker-sign-${r.folio}`}>
                            <SignatureIcon size={11} weight="bold" /> Firmar
                          </Button>
                        )}
                        {!r.signed_at && !blocked && user?.role === "admin" && (
                          <Button size="sm" className="h-7 bg-slate-800 hover:bg-slate-700 text-white text-xs gap-1"
                            onClick={() => openSign(r, "admin")} data-testid={`my-receipt-sign-${r.folio}`}>
                            <SignatureIcon size={11} weight="bold" /> Validar (admin)
                          </Button>
                        )}
                      </div>
                      {!r.signed_at && !blocked && user?.role !== "admin" && !r.published_at && (
                        <p className="text-[10px] italic text-slate-500 mt-1">Esperando publicación del administrador</p>
                      )}
                      {r.published_at && (
                        <p className="text-[10px] text-emerald-700 mt-0.5" data-testid={`my-receipt-published-${r.folio}`}>
                          ✓ Publicado por admin {r.published_at.slice(0, 10)}
                        </p>
                      )}
                      {r.worker_signed_at && (
                        <p className="text-[10px] text-emerald-800 font-bold mt-0.5" data-testid={`my-receipt-worker-signed-${r.folio}`}>
                          ✓ Firmado por el trabajador {r.worker_signed_at.slice(0, 10)}
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Banner de cláusulas del auditor — muestra el detalle al operador */}
                  {hasClauses && (
                    <div data-testid={`my-receipt-clauses-${r.folio}`}
                         className={`ml-14 border rounded-md p-2.5 text-xs ${
                      blocked ? "bg-red-50 border-red-300 text-red-900"
                              : "bg-amber-50 border-amber-300 text-amber-900"}`}>
                      <div className="flex items-start gap-2">
                        <Warning size={14} weight="fill" className={blocked ? "text-red-700 mt-0.5" : "text-amber-700 mt-0.5"} />
                        <div className="flex-1">
                          <p className="font-bold uppercase tracking-widest text-[10px]">
                            {blocked
                              ? "El auditor aplicó cláusulas de riesgo — este pago NO PROCEDE"
                              : "El auditor aplicó cláusulas de riesgo — pago reducido"}
                          </p>
                          <ul className="mt-1 space-y-0.5">
                            {(r.deductions || []).map((d) => (
                              <li key={d.key} className="flex justify-between font-mono">
                                <span>· {d.label}</span>
                                <span className="font-bold">
                                  -${d.amount.toLocaleString("es-MX", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                </span>
                              </li>
                            ))}
                          </ul>
                          <p className="mt-1 text-[10px] italic">
                            {blocked
                              ? "Las cláusulas aplicadas eliminan el pago completo del periodo. Comunícate con tu supervisor si necesitas aclarar."
                              : "El monto neto ya refleja el descuento por las cláusulas auditadas."}
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                </li>
                );
              })}
            </ul>
          </>
        )}
      </Section>

      <SignReceiptDialog
        open={!!signing} receipt={signing}
        sigData={sigData} setSigData={setSigData}
        sigNote={sigNote} setSigNote={setSigNote}
        busy={sigBusy}
        onCancel={() => setSigning(null)}
        onSubmit={submitSign} />
      <MyShiftDialog open={shiftDlgOpen} onOpenChange={setShiftDlgOpen} onSaved={load} />

      {/* Turnos del equipo — visible por operadores, supervisores y admin */}
      <TeamShiftsSection />
    </div>);
}

/**
 * TeamShiftsSection — Sección de "Turnos del equipo" en Mi Trabajo.
 * Muestra los turnos recientes de TODOS los operadores para que el siguiente
 * turno sepa cómo quedó cada equipo (operativo/no operativo + ubicación).
 * Accesible por operadores, supervisores y administrativos.
 */
function TeamShiftsSection() {
  const [days, setDays] = useState(3);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    setLoading(true);
    api.get(`/operations/team-shifts?days=${days}`)
      .then((r) => setRows(r.data || []))
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  }, [days]);

  const noOps = rows.filter((s) => s.end_status === "no_operativo");
  const shiftLabel = { dia: "Día", noche: "Noche" };
  return (
    <div className="bg-white border border-slate-200 rounded-lg p-4 space-y-3 mt-6" data-testid="team-shifts-section">
      <div className="flex flex-wrap items-center gap-2">
        <h2 className="font-heading font-black text-slate-900 text-lg">Turnos del equipo</h2>
        <span className="text-xs text-slate-500">
          Cómo quedó cada equipo · visible a todos los usuarios
        </span>
        <div className="ml-auto flex items-center gap-2">
          <Label className="text-[10px] uppercase font-bold text-slate-500">Últimos</Label>
          <select value={days} onChange={(e) => setDays(Number(e.target.value))}
                  className="h-8 px-2 border rounded text-xs bg-white" data-testid="team-shifts-days">
            <option value={1}>1 día</option>
            <option value={3}>3 días</option>
            <option value={7}>7 días</option>
            <option value={14}>14 días</option>
          </select>
        </div>
      </div>

      {noOps.length > 0 && (
        <div className="bg-red-50 border border-red-200 rounded-md p-2 text-xs text-red-800">
          <b>⚠ {noOps.length} equipo(s) quedaron NO operativos:</b>{" "}
          {noOps.slice(0, 5).map((s) => s.equipment_code || s.equipment_type).join(", ")}
          {noOps.length > 5 && ` y ${noOps.length - 5} más`}
        </div>
      )}

      {loading && <p className="text-xs text-slate-400 italic">Cargando…</p>}
      {!loading && rows.length === 0 && (
        <p className="text-xs text-slate-400 italic py-4 text-center">
          Sin turnos capturados en los últimos {days} días.
        </p>
      )}

      {!loading && rows.length > 0 && (
        <div className="overflow-x-auto border border-slate-100 rounded">
          <table className="min-w-full text-xs">
            <thead className="bg-slate-50 text-[10px] uppercase tracking-widest text-slate-500">
              <tr>
                <th className="p-2 text-left">Fecha · Turno</th>
                <th className="p-2 text-left">Equipo</th>
                <th className="p-2 text-left">Operador</th>
                <th className="p-2 text-left">Área</th>
                <th className="p-2 text-right">Horas</th>
                <th className="p-2 text-center">Fin de turno</th>
                <th className="p-2 text-left">Ubicación · Motivo</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {rows.slice(0, 60).map((s) => (
                <tr key={s.id} data-testid={`team-shift-${s.id}`}
                    className={s.end_status === "no_operativo" ? "bg-red-50/60" : "hover:bg-slate-50"}>
                  <td className="p-2 font-mono">{s.date} · {shiftLabel[s.shift] || s.shift}</td>
                  <td className="p-2">
                    <span className="font-semibold text-slate-900">{s.equipment_type}</span>
                    <span className="text-slate-500 ml-1">({s.equipment_code || "—"})</span>
                  </td>
                  <td className="p-2">
                    {s.operator_name || "—"}
                    {s.helper_name && <span className="text-slate-500 text-[10px]"> · ay. {s.helper_name}</span>}
                  </td>
                  <td className="p-2 text-slate-600">{s.area || "—"}</td>
                  <td className="p-2 text-right font-mono">{s.hours || 0}</td>
                  <td className="p-2 text-center">
                    {s.end_status === "no_operativo" ? (
                      <span className="text-[10px] uppercase tracking-widest font-black px-2 py-0.5 rounded-full bg-red-600 text-white">
                        ✗ No operativo
                      </span>
                    ) : (
                      <span className="text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800 border border-emerald-200">
                        ✓ Operativo
                      </span>
                    )}
                  </td>
                  <td className="p-2 text-slate-600">
                    {s.end_location || <span className="text-slate-300">—</span>}
                    {s.end_reason && (
                      <span className="text-red-600 block text-[10px]"><b>Motivo:</b> {s.end_reason}</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function MiniKpi({ label, value, tone = "slate", testid }) {
  const map = {
    slate: "bg-white border-slate-200 text-slate-900",
    emerald: "bg-emerald-50 border-emerald-200 text-emerald-800",
    amber: "bg-amber-50 border-amber-200 text-amber-800",
  }[tone];
  return (
    <div data-testid={testid} className={`${map} border rounded-lg p-2.5`}>
      <p className="text-[9px] uppercase tracking-widest font-bold text-slate-500">{label}</p>
      <p className="font-heading font-black text-lg leading-tight">{value}</p>
    </div>);
}

function Kpi({ testid, icon, label, value, sub, subCls }) {
  return (
    <div data-testid={testid} className="bg-white border border-slate-200 rounded-lg p-4">
      <div className="flex items-center gap-2 mb-2">
        {icon}
        <p className="overline-label">{label}</p>
      </div>
      <p className="font-heading font-black text-3xl text-slate-900">{value}</p>
      <p className={`text-xs mt-1 font-medium ${subCls || "text-slate-500"}`}>{sub}</p>
    </div>
  );
}

function Section({ title, testid, icon, count, children, action = null }) {
  return (
    <section data-testid={testid} className="bg-white border border-slate-200 rounded-lg">
      <header className="px-5 py-3 border-b border-slate-100 flex items-center gap-2">
        {icon}
        <h2 className="font-heading font-bold text-sm text-slate-900 uppercase tracking-wider">{title}</h2>
        <span className="text-xs text-slate-400 font-mono">({count})</span>
        {action && <div className="ml-auto">{action}</div>}
      </header>
      <div className="px-5">{children}</div>
    </section>
  );
}

function EmptyState({ testid, message }) {
  return (
    <p data-testid={testid} className="text-sm text-slate-400 italic py-6 text-center">{message}</p>
  );
}

// Signature dialog: reused component; mounted outside of MyWork's main JSX so
// it's not conditionally re-mounted on every state change.
export function SignReceiptDialog({ open, receipt, sigData, setSigData, sigNote, setSigNote, busy, onCancel, onSubmit }) {
  if (!receipt) return null;
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onCancel()}>
      <DialogContent className="bg-white max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2 text-slate-900">
            <SignatureIcon size={18} weight="bold" className="text-emerald-700" />
            Firmar recibo {receipt.folio}
          </DialogTitle>
        </DialogHeader>
        <div className="text-xs text-slate-600 space-y-1 border border-slate-200 rounded p-2.5 bg-slate-50">
          <p><b>Período:</b> {receipt.date_from} → {receipt.date_to}</p>
          <p><b>Días trabajados:</b> {receipt.days_worked}</p>
          <p><b>Bruto:</b> ${receipt.gross.toLocaleString("es-MX", { minimumFractionDigits: 2 })}
             {receipt.deductions_total > 0 && <> · <span className="text-rose-600">Descuentos: -${receipt.deductions_total.toLocaleString("es-MX", { minimumFractionDigits: 2 })}</span></>}
          </p>
          <p className="text-base font-heading font-black text-emerald-700 pt-1">
            NETO a recibir: ${receipt.net.toLocaleString("es-MX", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </p>
        </div>
        <div className="space-y-1">
          <p className="overline-label">Firma electrónica</p>
          <SignaturePad value={sigData} onChange={setSigData} width={480} height={120} testid="my-receipt-sig" />
        </div>
        <div className="space-y-1">
          <p className="overline-label">Nota opcional</p>
          <Textarea value={sigNote} onChange={(e) => setSigNote(e.target.value)}
            placeholder="Ej. Recibido conforme sin observaciones" rows={2} data-testid="my-receipt-sig-note" />
        </div>
        <p className="text-[10px] italic text-slate-500">
          Al firmar aceptas conforme el destajo del período. Se guardará con tu nombre y hora del servidor como evidencia.
        </p>
        <div className="flex gap-2 justify-end">
          <Button variant="outline" onClick={onCancel} data-testid="my-receipt-sig-cancel">
            <X size={14} weight="bold" /> Cancelar
          </Button>
          <Button onClick={onSubmit} disabled={busy || !sigData}
            className="bg-emerald-600 hover:bg-emerald-500 text-white" data-testid="my-receipt-sig-submit">
            {busy ? "Firmando…" : "Aceptar y firmar"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
